import { PayloadAction } from "@reduxjs/toolkit";
import { runMutation } from 'utils/graphQL';
import { FETCH_LOGGED_OUT_PLANS_CONFIG, FETCH_PAID_PLANS, INITIATE_CART_PAYMENT, UPDATE_USER_PROFILE, VALIDATE_COUPON } from "common/queries";
import { call, put, select, takeLatest } from "redux-saga/effects";
import { Pages } from "routes";
import { selectActiveWill, selectConfig, selectUserProfile, selectUserStatus, selectIsABCD } from "store/user/selectors";
import { addToUserStatus, updateActiveWill, updateProfile, UserStatus } from "store/user/slice";
import { runQuery } from "utils/graphQL";
import { InitiateCartPaymentResponse, InitiatePaymentResponse } from "utils/paymentSummaryTypes";
import { PaymentInput, UserProfile, Will, WILL_TYPES } from "utils/types";
import { addYessToCart, fetchPlanResponse, loadConfig, PaymentsPrerequisites, resetState, selectDOBNRI, selectedPlans, selectPromoCode, setConfigResponse, setDobNri, setPreRequisites, setPricingResponse, setPromoCode, setPurchasedPlans, setSelectedPlans, setUserOnboarding, clearPricingResponse } from ".";
import { ConfigResponse, FetchPaidPlansResponse, Plan } from "./types";
import { MemberDetails } from "Pages/NewPayments/MemberDetailsFormModal";
import { gql } from "@apollo/client";
import { PaymentReason } from "Pages/Payments";

function* reset() {
  yield put(resetState());
}

function* onPlansSelectedByQueryParams(action: PayloadAction<any>) {
  const queryParams = action.payload.params;
  const selectedPlans = queryParams["selectedPlans"];

  if (selectedPlans === undefined) {
    yield put(loadConfig());
    yield put(setPreRequisites(PaymentsPrerequisites.NO_PLANS_SELECTED));
    return;
  }

  const plansArray = selectedPlans.split(",");
  yield put(setSelectedPlans(plansArray));
  yield put(setPreRequisites(PaymentsPrerequisites.PLANS_SELECTED));
  yield put(fetchPlanResponse());
}

function* onUserLoggedOut(action: PayloadAction<UserStatus>) {
  if (action.payload === UserStatus.LOGGED_OUT) {
    yield put(setPreRequisites(PaymentsPrerequisites.NOT_LOGGED_IN));
  }

  if (action.payload === UserStatus.LOGGED_IN) {
    yield put(setPreRequisites(PaymentsPrerequisites.LOGGED_IN));
  }
}

function* onUserNotOnboarded(action: PayloadAction<UserStatus>) {
  if (action.payload !== UserStatus.PROFILE_LOADED) return;

  const userProfile: UserProfile = yield select(selectUserProfile);
  const userStatus: UserStatus[] = yield select(selectUserStatus);

  const isUserProfileIncomplete =
    userStatus.includes(UserStatus.PROFILE_INCOMPLETE) ||
    (userProfile.isNRI === undefined ||
      userProfile.isNRI === null);

  if (isUserProfileIncomplete) {
    yield put(setPreRequisites(PaymentsPrerequisites.NOT_ONBOARDED));
  } else {
    yield put(setPreRequisites(PaymentsPrerequisites.ONBOARDED));
  }
}

function* checkDOBNRIMatch() {
  const userStatus: UserStatus[] = yield select(selectUserStatus);
  const userProfile: UserProfile = yield select(selectUserProfile);
  const plansSelected: Plan[] = yield select(selectedPlans);

  if (userStatus.includes(UserStatus.LOGGED_OUT)) return;
  if (!userStatus.includes(UserStatus.PROFILE_LOADED)) return;

  const isUserProfileIncomplete =
    userStatus.includes(UserStatus.PROFILE_INCOMPLETE) ||
    (userProfile.isNRI === undefined ||
      userProfile.isNRI === null);
  if (isUserProfileIncomplete) return;

  const { dob, isNRI: userIsNRI }: { dob: string, isNRI: boolean } = yield select(selectDOBNRI);


  const activeWill: Will = yield select(selectActiveWill);

  const { isNRI } = userProfile;

  console.log({ isNRI, userIsNRI });
  if (dob === undefined || dob === null || userIsNRI === undefined || userIsNRI === null) {
    yield put(loadConfig());
    return;
  }
  const [date, month, year] = dob.split("/");

  const td = activeWill === null ? new Date(userProfile?.dateOfBirth as string) : new Date(activeWill.testatorDetails?.dateOfBirth as string) ;

  // const td = new Date(activeWill.testatorDetails?.dateOfBirth as string);
  // const td = new Date(userProfile?.dateOfBirth as string);
  // const td = userStatus.includes(UserStatus.WILL_DOC_NOT_CREATED) ? new Date(userProfile?.dateOfBirth as string) : new Date(activeWill.testatorDetails?.dateOfBirth as string);

  const newTDOB = new Date(td.getFullYear(), td.getMonth(), td.getDate()).valueOf();
  const userDOBString = new Date(`${year}/${month}/${date}`).valueOf();
  console.log({ newTDOB, userDOBString });
  if (newTDOB === userDOBString && isNRI === userIsNRI) {
    yield put(loadConfig());
    yield put(fetchPlanResponse());
    yield put(setPreRequisites(PaymentsPrerequisites.NO_DOB_ISNRI_MISMATCH));
    if (plansSelected.length === 0) {
      yield put(setPreRequisites(PaymentsPrerequisites.NO_PLANS_SELECTED));
    } else {
      yield put(setPreRequisites(PaymentsPrerequisites.PLANS_SELECTED));
    }
  } else {
    yield put(setPreRequisites(PaymentsPrerequisites.DOB_ISNRI_MISMATCH));
  }
}

function* onDOBNRI(action: PayloadAction<any>) {
  const queryParams = action.payload.params;
  const dob = queryParams["dob"];
  const isNRI = queryParams["isNRI"];

  if (dob !== undefined && isNRI !== undefined) {
    yield put(setDobNri({ dob, isNri: isNRI === "true" }));
  }
}

function* updateUserProfile(action: PayloadAction<Partial<MemberDetails>>) {
  console.log("updateUserProfile saga started", action.payload);
  try {
    //@ts-ignore
    const result: any = yield call(updateUserProfileRequest, action.payload);
    console.log("updateUserProfile saga: API result", result);
    if (!result) {
      console.error("updateUserProfile: API returned no result");
      return;
    }
    const { remainingProfileFields, profile } = result;
    const plansSelected: Plan[] = yield select(selectedPlans);
    yield put(updateProfile({ profile, remainingProfileFields }));

    if (remainingProfileFields.length === 0 && (profile.isNRI !== undefined || profile.isNRI !== null)) {
      yield put(loadConfig());
      yield put(fetchPlanResponse());
      yield put(setPreRequisites(PaymentsPrerequisites.ONBOARDED));
      if (plansSelected.length === 0) {
        yield put(setPreRequisites(PaymentsPrerequisites.NO_PLANS_SELECTED));
      } else {
        yield put(setPreRequisites(PaymentsPrerequisites.PLANS_SELECTED));
      }
    }
  } catch (error) {
    console.error("updateUserProfile saga error:", error);
  }
}

const updateUserProfileRequest = async ({ fullName, dateOfBirth, email, isNRI, mailingAddress }: Partial<MemberDetails>) => {
  console.log("updateUserProfileRequest called with:", { fullName, dateOfBirth, email, isNRI, mailingAddress });
  const {
    data: {
      updateUserProfile: { profile, remainingProfileFields },
    },
  }: {
    data: {
      updateUserProfile: { profile: UserProfile; remainingProfileFields: Array<string> };
    };
  } = await runMutation({
    mutation: UPDATE_USER_PROFILE,
    variables: {
      key: 'input',
      value: {
        ...(fullName ? { fullName: fullName } : {}),
        ...(dateOfBirth ? { dateOfBirth: new Date(dateOfBirth).toISOString() } : {}),
        ...(email ? { email: email } : {}),
        ...((isNRI === undefined || isNRI === null) ? {} : { isNRI: !isNRI }),
        ...(mailingAddress ? { mailingAddress: mailingAddress } : {}),
      },
    },
  });

  console.log("updateUserProfileRequest success:", { profile, remainingProfileFields });
  return { profile, remainingProfileFields };
}

function* onLoadConfig() {
  const { dob, isNRI }: { dob: string, isNRI: boolean } = yield select(selectDOBNRI);

  let mmddyyyyDOB = "";
  if (dob) {
    const [dd, mm, yyyy] = dob.split("/");
    mmddyyyyDOB = `${mm}/${dd}/${yyyy}`;
  }

  const response: ConfigResponse = yield call(() => runQuery({
    query: FETCH_LOGGED_OUT_PLANS_CONFIG,
    input: {
      key: 'input',
      value: {
        dob: mmddyyyyDOB || "10/10/1995",
        isNRI: isNRI || false,
      }
    }
  }));
  yield put(setConfigResponse(response));
  // Don't automatically call fetchPlanResponse here - let the component handle it after plans are set
}

export enum MainPlanModules {
  WILL = 'WILL',
  UNLIMITED_UPDATES = 'UNLIMITED_UPDATES',
  SMART = 'SMART',
  LAWYER_CALL = 'LAWYER_CALL',
  SHIPPING_AND_COURIER = 'SHIPPING_AND_COURIER',
  YESS = 'YESS'
}

export const planModuleToPlanId = {
  "lawyer_call": MainPlanModules.LAWYER_CALL,
  "smart": MainPlanModules.SMART,
  "unlimited_updates": MainPlanModules.UNLIMITED_UPDATES,
  "will": MainPlanModules.WILL,
  "shippingAndCourier": MainPlanModules.SHIPPING_AND_COURIER,
  "yess": MainPlanModules.YESS
}

function* onFetchPlanResponse() {
  console.log("------ns - onFetchPlanResponse-------")
  const plansSelected: Plan[] = yield select(selectedPlans);
  console.log("ns - plansSelected - saga: ", plansSelected);

  if (plansSelected.length === 0) return;

  const ALL_PLAN_IDS = ["starter", "pro", "smart", "yess"];
  const PLAN_IDS = ["starter", "pro"];

  // If plansSelected is 1
  // I'll use ALL_PLAN_IDS to segregate the selected cart Items

  // If plansSelected is greater than 1
  // I'll check if when filtered by ALL_PLAN_IDS if the length is still greater than 1
  // If it is, then I will use PLAN_IDS for plans and not of PLAN_IDS for modules
  // If it is not, then I will use ALL_PLAN_IDS

  const modules = plansSelected.filter(plan => ALL_PLAN_IDS.includes(plan.id)).length > 1 ? plansSelected.filter(plan => !PLAN_IDS.includes(plan.id)) : plansSelected.filter(plan => !ALL_PLAN_IDS.includes(plan.id));
  const plans = plansSelected.filter(plan => ALL_PLAN_IDS.includes(plan.id)).length > 1 ? plansSelected.filter(plan => PLAN_IDS.includes(plan.id)) : plansSelected.filter(plan => ALL_PLAN_IDS.includes(plan.id));


  // const modules = planSelected.length > 0 ?  : plansSelected.filter(plan => !PLAN_IDS.includes(plan.id));
  // const plans = plansSelected.filter(plan => PLAN_IDS.includes(plan.id));

  const cartItemsInput = plans.map(plan => ({
    itemId: plan.id,
    itemType: "PLAN"
  }));

  const modulesInput = modules.map(mod => ({
    //@ts-ignore
    id: planModuleToPlanId[mod.id],
    validityInYears: 1,
    useCount: mod.id === "smart" ? -1 : 1,
    enabled: true
  }));

  //@ts-ignore
  const willPrice : any = yield select(selectConfig);
  console.log("ns - willPrice - saga: ", willPrice);
  console.log("ns - modulesInput - saga: ", modulesInput);
  console.log("ns - cartItemsInput - saga: ", cartItemsInput);

  let promoCode: string | undefined = yield select(selectPromoCode);

  // Handle ABCD logic - apply coupon automatically if user is ABCD and no coupon is set
  const isABCD: boolean = yield select(selectIsABCD);
  if (isABCD && !promoCode) {
    console.log("ns - Applying ABCD coupon logic in saga");
    if (plansSelected.filter((item) => ['starter'].includes(item.id)).length > 0) {
      promoCode = 'HJUCAS';
      yield put(setPromoCode('HJUCAS'));
    } else if (plansSelected.filter((item) => ['pro'].includes(item.id)).length > 0) {
      promoCode = 'LBR1AP';
      yield put(setPromoCode('LBR1AP'));
    }
  }

  const couponInput = {
    couponCode: promoCode,
    couponType: "YELLOW_PLUS"
  }

  try {
    if(modulesInput.length === 1 && modulesInput[0].id === MainPlanModules.WILL){
      console.log("ns - paymentsSaga - onFetchPlanResponse - Will Update");
      const userProfile: UserProfile = yield select(selectUserProfile);
      
      let willUpdatePrice = 1;
      if(promoCode !== undefined){
        // @ts-ignore
        const sendDataToServer: any = yield call(() => runMutation({
          mutation: VALIDATE_COUPON,
          variables: {
            key: 'input',
            value: promoCode as string,
          },
        }));
        const { discount } = sendDataToServer.data.validateCouponByCode;
        const calcDiscountAmount = Math.round((willPrice!!.configs.mobilePlans['Update Premium Will'].basePrice / 100) * parseFloat(discount));

        willUpdatePrice = willUpdatePrice + (willPrice!!.configs.mobilePlans['Update Premium Will'].basePrice - calcDiscountAmount) * 1.18;
      }
      else{
        willUpdatePrice = willPrice!!.configs.mobilePlans['Update Premium Will'].grossAmount;
      }
      const currentWillId = userProfile.wills[userProfile.wills.length - 1].isActive ? userProfile.wills[userProfile.wills.length - 1].will_id : null;
      const cartResponse: InitiatePaymentResponse = yield call(() => runMutation({
        mutation: gql`
        mutation initiatePayment($input:PaymentInput!){
          initiatePayment(input:$input){
            orderId
            receiptId
          }
        }`,
        variables: {
          key: "input",
          value: {
            amount: willUpdatePrice * 100,
            willType: WILL_TYPES.PREMIUM_WILL,
            willId: currentWillId,
            paymentReason: 'Will Update',
            couponCode: promoCode ? promoCode : undefined
          }
        }
      }));
      console.log({ modules, plans });
      console.log({ cartResponse });
      yield put(setPricingResponse(cartResponse));
    }else{
      // CLEAR OLD DATA: Before creating new payment, clear stale cartResponse
      yield put(clearPricingResponse());
      console.log('SAGA: Cleared cartResponse before initiateCartPayment (with coupon)');
      
      const cartResponse: InitiateCartPaymentResponse = yield call(() => runMutation({
        mutation: INITIATE_CART_PAYMENT,
        variables: {
          key: "input",
          value: {
            cartItemsInput,
            modulesInput,
            couponInput: promoCode ? couponInput : undefined
          }
        }
      }));
      console.log({ modules, plans });
      console.log({ cartResponse });
      console.info("ns - modules: ", modules);
      console.info("ns - plans: ", plans);
      console.dir("ns - cartResponse: ", cartResponse);
      
      // TIMING DEBUG: Track when Redux state gets updated with new payment data
      const timestamp = new Date().toISOString();
      const orderId = cartResponse?.data?.initiateCartPayment?.orderId;
      console.log(`[${timestamp}] REDUX SAGA: About to update cartResponse with orderId: ${orderId}`);
      
      yield put(setPricingResponse(cartResponse));
      
      console.log(`[${new Date().toISOString()}] REDUX SAGA: setPricingResponse dispatched for orderId: ${orderId}`);
    }
  } catch (exc) {
    console.error(exc);
    if(modulesInput.length === 1 && modulesInput[0].id === MainPlanModules.WILL){
      const userProfile: UserProfile = yield select(selectUserProfile);
      const currentWillId = userProfile.wills[userProfile.wills.length - 1].isActive ? userProfile.wills[userProfile.wills.length - 1].will_id : null;
      const cartResponse: InitiatePaymentResponse = yield call(() => runMutation({
        mutation: gql`
        mutation initiatePayment($input:PaymentInput!){
          initiatePayment(input:$input){
            orderId
            receiptId
          }
        }`,
        variables: {
          key: "input",
          value: {
            amount: willPrice!!.configs.mobilePlans['Update Premium Will'].grossAmount * 100,
            willType: WILL_TYPES.PREMIUM_WILL,
            willId: currentWillId,
            paymentReason: 'Will Update',
            couponCode: promoCode ? promoCode : undefined
          }
        }
      }));
      console.log({ modules, plans });
      console.log({ cartResponse });
      yield put(setPricingResponse(cartResponse));
    }else{
      // CLEAR OLD DATA: Before creating new payment, clear stale cartResponse
      yield put(clearPricingResponse());
      console.log('SAGA: Cleared cartResponse before initiateCartPayment (without coupon)');
      
      const cartResponse: InitiateCartPaymentResponse = yield call(() => runMutation({
        mutation: INITIATE_CART_PAYMENT,
        variables: {
          key: "input",
          value: {
            cartItemsInput,
            modulesInput,
            couponInput: undefined
          }
        }
      }));
      if(cartResponse?.data?.initiateCartPayment?.couponDiscountAmount){
        cartResponse.data.initiateCartPayment.couponApplicationError = "-1";
      }
      
      // TIMING DEBUG: Track when Redux state gets updated for main cart payment flow
      const timestamp3 = new Date().toISOString();
      const orderId3 = cartResponse?.data?.initiateCartPayment?.orderId;
      console.log(`[${timestamp3}] REDUX SAGA (MAIN_CART): About to update cartResponse with orderId: ${orderId3}`);
      
      yield put(setPricingResponse(cartResponse));
      
      console.log(`[${new Date().toISOString()}] REDUX SAGA (MAIN_CART): setPricingResponse dispatched for orderId: ${orderId3}`);
    }
  }
}

function* onInitiatePayment(action: any) {
  const resolve = action?.meta?.resolve;
  const reject = action?.meta?.reject;
  
  try {
  const latestWill: Will = yield select(selectActiveWill);
  // console.log('ns - latestWill - saga: ', latestWill);
  const updatedWill: Will = {
    ...latestWill,
    paymentInfo: {
      paidWill: true,
      paymentId: "abcd",
      gracePeriod: ""
    }
  }
  // console.log('ns - updatedWill - saga: ', updatedWill);
  yield put(updateActiveWill({ will: updatedWill }));
  try {
    yield call((documentId: string) => runMutation({
      mutation: gql`
          mutation initiatePayment($input: PaymentInput!) {
            initiatePayment(input: $input) {
              orderId
              receiptId
            }
          }
        `,
      variables: {
        key: 'input',
        value: {
          amount: 0,
          willType: WILL_TYPES.PREMIUM_WILL,
          willId: documentId,
          paymentReason: PaymentReason.WILL_GENERATION,
        } as PaymentInput,
      },
    }), latestWill.documentId
    );
  } catch {
    console.log("Nothing here");
  }

  yield put({ type: "FETCH_USER_PROFILE" });
  
  if (resolve) yield call(resolve);
  } catch (err) {
    console.log('Error in initiatePayment saga:', err);
    if (reject) {
      yield call(reject, err);
    }
  }
}

function* onLoadUsersPlans(action: any) {
  const resolve = action?.meta?.resolve;
  const reject = action?.meta?.reject;
  
  try {
    const paidPlansResponse: FetchPaidPlansResponse = yield call(() => runQuery({ query: FETCH_PAID_PLANS }));
    yield put(setPurchasedPlans(paidPlansResponse.data.getPaidItems));
    
    if (resolve) yield call(resolve);
  } catch (err) {
    console.log('Error in loadUsersPlans saga:', err);
    if (reject) {
      yield call(reject, err);
    }
  }
}

function* onPlansSelected() {
  yield put(setPreRequisites(PaymentsPrerequisites.PLANS_SELECTED));
  yield put(fetchPlanResponse());
}

function* onLocationChanged(action: PayloadAction<any>) {
  if (action.payload.trackedPageName !== Pages.PAYMENT) {
    yield reset();
    return;
  }

  // Always load config so NOT_ONBOARDED / NO_PLANS_SELECTED screens can render
  yield put(loadConfig());
  yield onPlansSelectedByQueryParams(action);
  yield onDOBNRI(action);
  // Note: onboarding check (onUserNotOnboarded) runs via onAddToUserStatusChanged
  // when PROFILE_LOADED fires, so no need to call it here.
}

function* onAddToUserStatusChanged(action: any) {
  yield onUserLoggedOut(action);
  yield onUserNotOnboarded(action);
  yield checkDOBNRIMatch();
}

export default function* paymentsSaga() {
  yield takeLatest(setSelectedPlans.type, onPlansSelected);
  yield takeLatest("LOCATION_CHANGED", onLocationChanged);
  yield takeLatest(addToUserStatus.type, onAddToUserStatusChanged);
  yield takeLatest(loadConfig.type, onLoadConfig);
  yield takeLatest(fetchPlanResponse.type, onFetchPlanResponse);
  yield takeLatest(setUserOnboarding.type, updateUserProfile);
  yield takeLatest(addYessToCart.type, onPlansSelected);
  yield takeLatest(setPromoCode.type, onFetchPlanResponse);
  yield takeLatest('payments/initiatePayment', onInitiatePayment);
  yield takeLatest('payments/loadUsersPlans', onLoadUsersPlans);
}

